import type { User } from "oidc-client-ts";
import type React from "react";

import { getFeature } from "@asurion-core/expert-workspace-feature-flagging";
import { getUserManagerSettings } from "@expert/authentication";
import { useEffect, useRef, useState } from "react";
import { AuthProvider, hasAuthParams, useAuth } from "react-oidc-context";
import { useNavigate } from "react-router";

import { setIdentityAsTestUser } from "@/analytics";
import { GlobalError } from "@/common-ui";
import { WorkspaceLoader } from "@/common-ui/WorkspaceLoader";
import { env } from "@/config";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { USER_FRIENDLY_NAME_QS_KEY } from "@/shared-utils/constants";
import { endDynatraceAction, startDynatraceAction } from "@/utils/RealUserMonitoringClient";

import { ErrorText } from "./elements";

const LOGIN_REQUIRED_ERROR_TYPE = "Login_required";

interface AuthenticationProviderProps {
    children?: React.ReactNode;
}

interface AuthHandlerProps {
    children?: React.ReactNode;
}

function useOnSigninCallback() {
    const navigate = useNavigate();
    return (user?: User) => void navigate(user?.url_state ?? globalThis.location.pathname, { replace: true });
}

export function AuthenticationProvider({ children }: AuthenticationProviderProps) {
    const onSigninCallback = useOnSigninCallback();
    return (
        <AuthProvider
            {...getUserManagerSettings({
                oidcSsoUrl: env.oidcSsoUrl,
                oidcSsoClientId: env.oidcSsoClientId,
                oidcSsoAdditionalScope: env.oidcSsoAdditionalScope,
                employeeId: new URLSearchParams(window.location.search).get(USER_FRIENDLY_NAME_QS_KEY) ?? undefined,
                isDev: import.meta.env.VITE_ENV_TYPE === "development",
            })}
            onSigninCallback={onSigninCallback}
        >
            <AuthHandler>{children}</AuthHandler>
        </AuthProvider>
    );
}

function AuthHandler({ children }: AuthHandlerProps) {
    const logger = useLogger({ module: "AuthenticationProvider" });
    const auth = useAuth();
    const [checkingIfTestUser, setCheckingIfTestUser] = useState<boolean>(true);
    const loginTracked = useRef(false);

    useEffect(() => {
        if (!hasAuthParams() && !auth.isAuthenticated && !auth.isLoading && !auth.activeNavigator) {
            logger.debug({ auth }, "auth login redirect");
            startDynatraceAction("Login");
            void auth.signinRedirect({
                nonce: crypto.randomUUID(),
                url_state: `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`,
            });
        }

        auth.events.addAccessTokenExpiring((event) => {
            logger.info({ event }, "Token expiring soon...");
        });

        auth.events.addSilentRenewError((error) => {
            logger.info({ error }, "Silent renew error, redirecting to login");
            void auth.signinRedirect({
                nonce: crypto.randomUUID(),
            });
        });
    }, [auth, logger]);

    useEffect(() => {
        if (auth.isLoading) {
            return;
        }

        const checkIfUserIsTestUser = async () => {
            const isTestUser = await getFeature<boolean>("EWP-isTestUser", {
                environment: env.mode,
                channel: env.channel,
                employeeId: auth.user?.profile.employeeID ?? auth.user?.profile.userPrincipalName ?? "",
            });

            logger.info(`(Worker): isTestUser: '${isTestUser}'`);
            if (isTestUser) {
                setIdentityAsTestUser();
            }

            setCheckingIfTestUser(false);
        };

        void checkIfUserIsTestUser();
    }, [auth.isLoading, auth.user, logger]);

    switch (auth.activeNavigator) {
        case "signinSilent":
            return <WorkspaceLoader msg="Signing you in..." />;
        case "signoutRedirect":
            return <WorkspaceLoader msg="Signing you out..." />;
        case undefined:
        case "signinRedirect":
        case "signinPopup":
        case "signoutPopup":
        case "signoutSilent":
        case "signinResourceOwnerCredentials":
            break;
    }

    if (auth.error) {
        if ((auth.error.innerError as Error & { error: string }).error === LOGIN_REQUIRED_ERROR_TYPE) {
            return <WorkspaceLoader msg="Signing you in..." />;
        }

        if (!loginTracked.current) {
            loginTracked.current = true;
            endDynatraceAction("Login", { error: auth.error.message });
        }
        return <GlobalError title="An error occurred while signing you in" />;
    }

    if (auth.isLoading) return <WorkspaceLoader msg="Signing you in..." />;
    if (checkingIfTestUser) return <WorkspaceLoader msg="Checking identity..." />;
    if (auth.isAuthenticated) {
        if (!loginTracked.current) {
            loginTracked.current = true;
            endDynatraceAction("Login");
        }
        return <>{children} </>;
    }
    return <ErrorText>Not Authenticated</ErrorText>;
}
